import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TpETLObjectTypes } from '../../etl-data-types/interfaces';
import { TpETLComponentTypeManifest } from '../interfaces';
import {
  TpETLPipelineComponent,
  TpETLDryRun,
  TpETLDryRunComponentResult,
  TpETLDryRunMatrixObject,
  isDryRunMatrixObject,
} from '../../interfaces';
import { getETLComponentInputObject, addDryRunObject } from '../../utils';
import { addETLDryRunError, getDryRunErrorsForComponent } from '../../etlDryRun';
import { CONFIGPARAMTYPES } from '../../../visual-analytics/helpers/configParams';
import { VALTYPES } from '../../../../data/interfaces';
import {
  copyETLDryRunObjectMatrix,
  createConfigParamTypeExistingMatrixColumn,
  createConfigParamTypeExistingMatrixColumnList,
} from '../utils';

import { get_guid } from '../../../visual-analytics/helpers/misc';
import styles from '../../edit-etl-component/editETLComponentForm.module.scss';
import ParamSet from '../../edit-etl-component/ParamSet';
import { FancyButton } from '../../../../components/primitives/FancyButton';
import { parseETLContrastTermExpression } from './etlContrastTermParser';

interface TpContrastInfo {
  id: string;
  name: string;
  term: string;
}

function performDryRun(dryRun: TpETLDryRun, comp: TpETLPipelineComponent): TpETLDryRunComponentResult {
  const matrix: TpETLDryRunMatrixObject = {
    type: TpETLObjectTypes.MATRIX,
    id: comp.outputObjectIds['result'],
    createdByCompId: comp.id,
    obsMetadataColumns: [],
    featureMetadataColumns: [],
    linkColumnId: '',
  };
  addDryRunObject(dryRun, matrix);

  const input = getETLComponentInputObject(dryRun, comp, 'source');
  if (!isDryRunMatrixObject(input)) {
    addETLDryRunError(dryRun, `Input is not a matrix`, comp.id, 'source');
    return { abort: false };
  }

  // Set the metadata columns based on the input matrix
  copyETLDryRunObjectMatrix(input, matrix);

  // Check if the required parameters are present
  const condition = comp.params['condition'];
  if (!condition) {
    addETLDryRunError(dryRun, `Condition parameter is required`, comp.id, 'condition');
  }

  // Check range of p-value threshold
  const pValueThreshold = comp.params['p_value_threshold'];
  if (pValueThreshold !== undefined && (pValueThreshold < 0 || pValueThreshold > 1)) {
    addETLDryRunError(dryRun, `P-value threshold must be between 0 and 1`, comp.id, 'p_value_threshold');
  }

  // Add the contrast columns
  const contrasts = comp.params['contrasts'];
  const designColumns = comp.params['design_columns'] || [];
  const conditionColumn = condition ? [condition] : [];
  const allowedColumns = [...conditionColumn, ...designColumns];
  if (!contrasts || !Array.isArray(contrasts) || contrasts.length === 0) {
    addETLDryRunError(dryRun, `At least one contrast must be defined`, comp.id, 'contrasts');
    return { abort: false };
  }
  for (const contrast of contrasts as TpContrastInfo[]) {
    if (!contrast.name || !contrast.term) {
      addETLDryRunError(dryRun, `Contrast must have a name and term`, comp.id, contrast.id);
      continue;
    }
    if (/^[a-zA-Z0-9_]+$/.test(contrast.name) === false) {
      addETLDryRunError(dryRun, `Contrast names should be alphanumeric and underscores only`, comp.id, contrast.id);
      continue;
    }
    try {
      parseETLContrastTermExpression(allowedColumns, contrast.term);
    } catch (error) {
      addETLDryRunError(dryRun, String(error), comp.id, contrast.id);
    }
  }
  const contrastNames = contrasts.map((contrast: TpContrastInfo) => contrast.name);

  // Sample metadata columns
  matrix.featureMetadataColumns = [
    ...matrix.featureMetadataColumns,
    {
      identifier: 'condition_contrast',
      name: 'Condition contrast',
      description: 'All applicable contrasts.',
      dataType: VALTYPES.MULTICAT,
    },
    ...contrastNames.map((name: string) => ({
      identifier: `contrast_${name.toLocaleLowerCase()}`,
      name: `Contrast ${name}`,
      description: `Sample is involved in contrast ${name}`,
      dataType: VALTYPES.NUMBER,
    })),
  ];

  matrix.obsMetadataColumns = [
    ...matrix.obsMetadataColumns,
    {
      identifier: 'significant_in',
      name: 'Significant contrasts',
      description: 'List of contrasts that are both significant and biologically relevant.',
      dataType: VALTYPES.MULTICAT,
    },
    {
      identifier: 'regulated_in',
      name: 'Up/down regulated contrasts',
      description: 'List of contrasts that are up or down regulated.',
      dataType: VALTYPES.MULTICAT,
    },
    ...contrastNames.flatMap((name: string) => [
      {
        identifier: `contrast_${name.toLocaleLowerCase()}_mean`,
        name: `Contrast ${name} average expression`,
        description: `Average log2-expression ${name}`,
        dataType: VALTYPES.NUMBER,
      },
      {
        identifier: `contrast_${name.toLocaleLowerCase()}_logfc`,
        name: `Contrast ${name} log2 fold change`,
        description: `Log2 fold change for contrast ${name}`,
        dataType: VALTYPES.NUMBER,
      },
      {
        identifier: `contrast_${name.toLocaleLowerCase()}_pvalue`,
        name: `Contrast ${name} p-value`,
        description: `P-value for contrast ${name}`,
        dataType: VALTYPES.NUMBER,
      },
      {
        identifier: `contrast_${name.toLocaleLowerCase()}_padj`,
        name: `Contrast ${name} adjusted p-value`,
        description: `Adjusted p-value for contrast ${name}`,
        dataType: VALTYPES.NUMBER,
      },
      {
        identifier: `contrast_${name.toLocaleLowerCase()}_neglogpvalue`,
        name: `Contrast ${name} -log10(p-value)`,
        description: `-log p-value for contrast ${name}`,
        dataType: VALTYPES.NUMBER,
      },
      {
        identifier: `contrast_${name.toLocaleLowerCase()}_neglogpadj`,
        name: `Contrast ${name} -log10(adjusted p-value)`,
        description: `-log10 of Adjusted P-value for contrast ${name}`,
        dataType: VALTYPES.NUMBER,
      },
      {
        identifier: `contrast_${name.toLocaleLowerCase()}_significant`,
        name: `Contrast ${name} significant`,
        description: `Indicates if the contrast ${name} is significant`,
        dataType: VALTYPES.CATEGORICAL,
      },
      {
        identifier: `contrast_${name.toLocaleLowerCase()}_regulated`,
        name: `Contrast ${name} up/down regulated`,
        description: `Indicates if the contrast ${name} is up or down regulated`,
        dataType: VALTYPES.CATEGORICAL,
      },
    ]),
  ];

  return {
    abort: false,
  };
}

function renderCustomConfigParams(
  dryRun: TpETLDryRun,
  comp: TpETLPipelineComponent,
  updateEditingComponent: (newComp: TpETLPipelineComponent) => void,
): any {
  const contrasts = (comp.params['contrasts'] || []) as TpContrastInfo[];
  const errors = getDryRunErrorsForComponent(dryRun, comp.id);
  const contrastErrors = errors.filter((error: any) => error.paramId == 'contrasts');

  function updateParam(contrastId: string, paramId: string, newValue: any) {
    const contrastIdx = contrasts.map((contrastInfo) => contrastInfo.id).indexOf(contrastId);
    if (contrastIdx < 0) throw `Invalid contrast id ${contrastId}`;
    else if (paramId == 'name') contrasts[contrastIdx].name = newValue;
    else if (paramId == 'term') contrasts[contrastIdx].term = newValue;
    updateEditingComponent(comp);
  }

  function renderContrastErrors(contrast: TpContrastInfo) {
    const contrastErrors = errors.filter((error: any) => error.paramId == contrast.id);
    if (contrastErrors.length == 0) return null;
    return (
      <div className={styles.errors}>
        {contrastErrors.map((error, idx) => (
          <div key={`errors_${contrast.id}_${idx}`}>{error.message}</div>
        ))}
      </div>
    );
  }

  return (
    <div>
      {contrastErrors.length > 0 && (
        <div className={styles.errors}>
          {contrastErrors.map((error, idx) => (
            <div key={`errors_contrasts_${idx}`}>{error.message}</div>
          ))}
        </div>
      )}
      {contrasts.map((contrastInfo) => (
        <div key={contrastInfo.id} className={styles.section}>
          <h3>
            Contrast
            <button
              onClick={() => {
                comp.params['contrasts'] = contrasts.filter((filterContrast) => filterContrast.id !== contrastInfo.id);
                updateEditingComponent(comp);
              }}
            >
              <FontAwesomeIcon icon="xmark" />
            </button>
          </h3>
          {renderContrastErrors(contrastInfo)}
          <div>Terms can consist of operators and column names, e.g. "column1.value1 - column2.value2".</div>

          <ParamSet
            params={[
              {
                name: 'Contrast name (column identifier)',
                component: (
                  <input
                    value={contrastInfo.name}
                    onChange={(ev) => {
                      updateParam(contrastInfo.id, 'name', ev.target.value);
                    }}
                  />
                ),
              },
              {
                name: 'Term',
                component: (
                  <input
                    value={contrastInfo.term}
                    onChange={(ev) => {
                      updateParam(contrastInfo.id, 'term', ev.target.value);
                    }}
                  />
                ),
              },
            ]}
          />
        </div>
      ))}

      <FancyButton
        iconName="fas:plus"
        onClick={() => {
          const newContrast: TpContrastInfo = {
            id: get_guid(),
            name: '',
            term: '',
          };
          comp.params['contrasts'] = [...contrasts, newContrast];
          updateEditingComponent(comp);
        }}
      >
        Add contrast
      </FancyButton>
    </div>
  );
}

export const manifestETLDgeAnalysis: TpETLComponentTypeManifest = {
  id: 'DgeAnalysis',
  name: 'DGE Analysis',
  description: 'Perform DGE analysis on matrix data.',
  categoryId: 'matrix',
  icon: <FontAwesomeIcon icon={['far', 'volcano']} />,
  performDryRun,
  renderCustomConfigParams,
  inputs: [
    {
      id: 'source',
      name: 'Source matrix',
      type: TpETLObjectTypes.MATRIX,
      description: 'Matrix containing normalized feature observations.',
      obsMetadataColumns: [],
      featureMetadataColumns: [],
    },
  ],
  outputs: [
    {
      type: TpETLObjectTypes.MATRIX,
      id: 'result',
      name: 'DGE results',
      description: 'Matrix with DGE contrast results.',
      obsMetadataColumns: [],
      featureMetadataColumns: [],
    },
  ],
  params: [
    {
      // Todo: Filter on column type: categorical
      id: 'condition',
      name: 'Design condition column',
      description: 'Main design variable.',
      paramType: createConfigParamTypeExistingMatrixColumn('source', 'features'),
    },
    {
      // Todo: Filter on column type: categorical
      id: 'subject',
      name: 'Subject column',
      description: 'Optional subject representing sample blocks.',
      paramType: createConfigParamTypeExistingMatrixColumn('source', 'features'),
    },
    {
      // Todo: filter column types: categorirical
      id: 'design_columns',
      name: 'Design columns',
      description: 'Additional design columns to include in the analysis.',
      paramType: createConfigParamTypeExistingMatrixColumnList('source', 'features'),
    },
    {
      id: 'logfc_threshold',
      name: 'Log fold change threshold',
      description: 'The log fold change threshold (absolute value) for biological relevance.',
      paramType: {
        configParamTypeId: CONFIGPARAMTYPES.Numerical,
        defaultValue: 1.5,
      },
    },
    {
      id: 'p_value_threshold',
      name: 'P-value threshold',
      description: 'The p-value threshold for significance.',
      paramType: {
        configParamTypeId: CONFIGPARAMTYPES.Numerical,
        defaultValue: 0.05,
      },
    },
  ],
};
